/**
 * CheckInComplianceCard Component
 * Displays check-in compliance metrics with breakdown
 *
 * Story 3.3: Check-in Compliance Rate
 */

import React from 'react';
import { clsx } from 'clsx';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { Card } from '../../common';
import { OrgBaselineNote } from '../ReportScopeIndicator';
import type { CheckInComplianceBaseline } from '../../../services/reportApi';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface CheckInComplianceData {
  overallCompliance: number;
  onTime: number;
  late: number;
  missed: number;
  byDepartment: Array<{ departmentId: string | null; department: string; compliance: number; total: number }>;
}

export interface CheckInComplianceCardProps {
  /** Compliance data */
  data: CheckInComplianceData;
  /** Loading state */
  isLoading?: boolean;
  /** Compliance threshold for "good" status */
  threshold?: number;
  /**
   * Resolved scope for these figures, so the zero state can say whose
   * check-ins are missing. Resolved by the hook layer.
   */
  scope?: ResolvedScope;
  /**
   * Organization-wide comparison for a scoped caller. Carries the headline rate
   * only — deliberately NOT `byDepartment`, since handing back the whole
   * organization's per-department breakdown would restore exactly the
   * cross-team detail the scoping removed.
   */
  orgBaseline?: CheckInComplianceBaseline;
  /** Additional class names */
  className?: string;
}

/**
 * Get compliance color based on value
 */
function getComplianceColor(compliance: number, threshold: number): string {
  if (compliance >= threshold) return 'text-green-600';
  if (compliance >= threshold - 20) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Get compliance background color
 */
function getComplianceBgColor(compliance: number, threshold: number): string {
  if (compliance >= threshold) return 'bg-green-500';
  if (compliance >= threshold - 20) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Loading skeleton
 */
const ComplianceSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-center mb-6">
      <div className="w-32 h-32 rounded-full bg-stone-200" />
    </div>
    <div className="grid grid-cols-3 gap-4 mb-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center">
          <div className="h-6 bg-stone-200 rounded w-12 mx-auto mb-2" />
          <div className="h-3 bg-stone-200 rounded w-16 mx-auto" />
        </div>
      ))}
    </div>
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-8 bg-stone-200 rounded" />
      ))}
    </div>
  </div>
);

/**
 * Check-in Compliance Card Component
 */
export const CheckInComplianceCard: React.FC<CheckInComplianceCardProps> = ({
  data,
  isLoading = false,
  threshold = 80,
  scope,
  orgBaseline,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Check-in Compliance</h3>
        <ComplianceSkeleton />
      </Card>
    );
  }

  // Nothing was expected, so nothing was missed. Without this the gauge renders
  // a red 0% "Compliance" — indistinguishable from a team that skipped every
  // check-in it owed.
  const hasExpectedCheckIns = data.onTime + data.late + data.missed > 0;
  if (!hasExpectedCheckIns) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Check-in Compliance</h3>
        <div className="text-center py-8 text-stone-500">
          <Clock className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>
            {scope?.isSelfOnly
              ? 'No check-ins were due on your OKRs in this period'
              : scope?.kind === 'team'
                ? 'No check-ins were due for your team in this period'
                : 'No check-ins were due in this period'}
          </p>
        </div>
        {orgBaseline && (
          <OrgBaselineNote
            className="justify-center"
            value={`${orgBaseline.overallCompliance}%`}
            label="compliance"
          />
        )}
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Check-in Compliance</h3>

      {/* Overall compliance gauge */}
      <div className="flex items-center justify-center mb-6">
        <div className="relative w-32 h-32">
          {/* Background circle */}
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              fill="none"
              stroke="#e7e5e4"
              strokeWidth="12"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              fill="none"
              stroke={data.overallCompliance >= threshold ? '#22c55e' : data.overallCompliance >= threshold - 20 ? '#f59e0b' : '#ef4444'}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={`${(data.overallCompliance / 100) * 352} 352`}
            />
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={clsx('text-3xl font-bold', getComplianceColor(data.overallCompliance, threshold))}>
              {data.overallCompliance}%
            </span>
            <span className="text-xs text-stone-500">Compliance</span>
          </div>
        </div>
      </div>

      {/* Breakdown stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xl font-bold">{data.onTime}</span>
          </div>
          <p className="text-xs text-stone-500">On Time</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xl font-bold">{data.late}</span>
          </div>
          <p className="text-xs text-stone-500">Late</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
            <XCircle className="w-4 h-4" />
            <span className="text-xl font-bold">{data.missed}</span>
          </div>
          <p className="text-xs text-stone-500">Missed</p>
        </div>
      </div>

      {/* Organization comparison for a scoped caller (absent for an admin) */}
      {orgBaseline && (
        <OrgBaselineNote
          className="justify-center -mt-2 mb-6"
          value={`${orgBaseline.overallCompliance}%`}
          label="compliance"
        />
      )}

      {/* Department breakdown */}
      {data.byDepartment.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-stone-700 mb-3">By Department</h4>
          <div className="space-y-2">
            {data.byDepartment.slice(0, 5).map((dept) => (
              <div key={dept.departmentId ?? 'no-department'} className="flex items-center gap-3">
                <span className="text-sm text-stone-600 w-24 truncate" title={dept.department}>
                  {dept.department}
                </span>
                <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full', getComplianceBgColor(dept.compliance, threshold))}
                    style={{ width: `${dept.compliance}%` }}
                  />
                </div>
                <span className={clsx('text-sm font-medium w-12 text-right', getComplianceColor(dept.compliance, threshold))}>
                  {dept.compliance}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default CheckInComplianceCard;
